import React from "react";
import { Link } from "react-router-dom";
import { Heart, Users, BedDouble, Star, MapPin } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { fmtPrice } from "@/lib/constants";
import { toast } from "sonner";

const VillaCard = ({ villa, index = 0 }) => {
  const { isFavorite, toggleFavorite } = useAuth();
  const { t, localePath } = useLanguage();
  const fav = isFavorite(villa.id);
  const img = villa.card_image || villa.images?.[0]?.url;

  const onFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const res = await toggleFavorite(villa.id);
    if (res?.needLogin) {
      toast.error("Please sign in to save favourites");
    } else {
      toast.success(res.action === "added" ? "Added to favourites" : "Removed from favourites");
    }
  };

  return (
    <Link
      to={localePath(`/villas/${villa.slug}`)}
      data-testid="villa-card"
      className="group block rounded-[18px] border border-border bg-white overflow-hidden hover-lift"
    >
      <div className="relative">
        <AspectRatio ratio={4 / 3}>
          {img ? (
            <img
              src={img}
              alt={villa.images?.[0]?.alt || villa.name}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-500 will-change-transform group-hover:scale-[1.04]"
            />
          ) : (
            <div className="h-full w-full bg-secondary" />
          )}
        </AspectRatio>
        {villa.featured && (
          <Badge className="absolute top-3 left-3 bg-[var(--lux-champagne)] text-[var(--lux-ink)] border-0 font-medium">Featured</Badge>
        )}
        <button
          onClick={onFav}
          data-testid="villa-card-favorite-button"
          aria-label="Save to favourites"
          className="absolute top-3 right-3 h-9 w-9 rounded-full glass flex items-center justify-center hover:scale-110 transition-transform"
        >
          <Heart className={`h-4 w-4 ${fav ? "fill-[var(--lux-seaglass)] text-[var(--lux-seaglass)]" : "text-[var(--lux-ink)]"}`} />
        </button>
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {villa.area}</span>
          <span className="flex items-center gap-1 text-xs font-medium"><Star className="h-3.5 w-3.5 fill-[var(--lux-champagne)] text-[var(--lux-champagne)]" /> {villa.rating}</span>
        </div>
        <h3 className="font-display text-2xl leading-tight mt-1">{villa.name}</h3>
        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
          <span className="flex items-center gap-1"><Users className="h-4 w-4" /> {villa.max_guests}</span>
          <span className="flex items-center gap-1"><BedDouble className="h-4 w-4" /> {villa.bedrooms} {t("card.beds")}</span>
        </div>
        <div className="mt-4 flex items-end justify-between">
          <div>
            <span className="font-display text-xl">{fmtPrice(villa.price_per_night)}</span>
            <span className="text-xs text-muted-foreground"> {t("card.perNight")}</span>
          </div>
          <span className="text-xs text-[var(--lux-seaglass)] font-medium">{t("card.minNights", { n: villa.min_nights })}</span>
        </div>
      </div>
    </Link>
  );
};

export default VillaCard;
